import React, { useState } from "react";
import {
  Box,
  IconButton,
  VStack,
  HStack,
  Text,
  Tooltip,
  Icon,
  CloseButton,
  Divider,
  Button,
  Circle,
} from "@chakra-ui/react";
import { AddIcon, ChevronLeftIcon, DeleteIcon } from "@chakra-ui/icons";
import { FaClipboardList } from "react-icons/fa";
import { FaLinesLeaning } from "react-icons/fa6";
import { cancelTransaction } from "../../services/AnalysisTreeServices";
import AddFromLibraryModal from "./AddFromLibraryModal";
import { ExportContextProvider } from "context/export/ExportContext";

interface AnalysisNavigationBarProps {
  isNavOpen: boolean;
  setIsNavOpen: (isOpen: boolean) => void;
  responseData: Record<string, any>;
  setResponseData: (data: Record<string, any>) => void;
  visibleUuid: string | null;
  setSelectedUuid: (uuid: string) => void;
  handleDeleteTree: (uuid: string) => void;
  btnRef: React.RefObject<HTMLButtonElement>;
  onOpen: () => void;
}

const AnalysisNavigationBar: React.FC<AnalysisNavigationBarProps> = ({
  isNavOpen,
  setIsNavOpen,
  responseData,
  setResponseData,
  visibleUuid,
  setSelectedUuid,
  handleDeleteTree,
  btnRef,
  onOpen,
}) => {
  
  const [isOpen, setIsOpen] = useState(false);

  const openAddFromLibraryModal = () => setIsOpen(true);

  const closeAddFromLibraryModal = () => setIsOpen(false);
  
  const hasTrees = Object.keys(responseData).length > 0;

  return (
    <ExportContextProvider>
    <Box
      position="fixed"
      left={0}
      top="50%"
      transform="translateY(-50%)"
      height="auto"
      width="auto"
      transition="all 0.3s ease-in-out"
      zIndex={100}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {!isNavOpen && (
        <IconButton
          aria-label="side-navigation-menu"
          size="xl"
          icon={isNavOpen ? <ChevronLeftIcon boxSize={8} /> : <FaLinesLeaning size={27} />}
          onClick={() => setIsNavOpen(!isNavOpen)}
          zIndex={100}
          bg="purple.700"
          color="white"
          borderRadius="xl"
          onMouseEnter={() => setIsNavOpen(true)}
          ml={-2}
        />
      )}
      {isNavOpen && (
        <Box
          position="absolute"
          left={0}
          width="270px"
          bg="gray.50"
          p={3}
          borderRadius="md"
          onMouseLeave={() => setIsNavOpen(false)}
          boxShadow="md"
          transform={`translateX(${isNavOpen ? "0" : "-100%"})`}
          opacity={isNavOpen ? 1 : 0}
          transition="all 1s ease-in-out"
          borderWidth={2}
          borderColor="gray.300"
          maxHeight="90vh"
          overflow="hidden"
        >
          <Box p={1} borderRadius="lg" w="100%" h="100%">
            <VStack spacing={2} align="center" h="100%" justify="space-between">
              <VStack
                spacing={2}
                width="100%"
              >
                <HStack spacing={1} align="center" justify="space-between" w="100%" px={1}>
                  <HStack spacing={2} align="center" px={1}>
                    <Icon as={FaClipboardList} color="purple.700" boxSize={6} />
                    <Text fontSize="md" fontWeight={700} color="purple.700" mb={0} textAlign="left">
                      ANALYSES
                    </Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Tooltip
                      label="Clear all analyses"
                      hasArrow
                      placement="right"
                      bg="gray.50"
                      color="black"
                      fontSize="sm"
                      p={2}
                    >
                      <IconButton
                        aria-label="Clear all"
                        icon={<DeleteIcon />}
                        size="sm"
                        color="gray.400"
                        _hover={{
                          color: "red.500",
                          bg: "purple.50"
                        }}
                        onClick={() => {
                          const cancelRequests = Object.keys(responseData).map(uuid => ({
                            state_key: [{ transaction_id: uuid }]
                          }));
                          
                          Promise.all(cancelRequests.map(req => cancelTransaction(req)))
                            .then(() => {
                              setResponseData({});
                            })
                            .catch(error => {
                              console.error("Error deleting trees:", error);
                            });
                        }}
                      />
                    </Tooltip>
                  </HStack>
                </HStack>
                <Divider m={0} borderColor="purple.700" borderWidth="1px" />
                <VStack
                  css={{
                    scrollbarWidth: "thin",
                    scrollbarColor: "#A0AEC0 #E2E8F0",
                    "&::-webkit-scrollbar": {
                      width: "5px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#E2E8F0",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#A0AEC0",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#718096",
                    }
                  }}
                  width="100%"
                  overflowY="auto"
                  maxHeight="65vh"
                >
                  {hasTrees &&
                    Object.keys(responseData).map((uuid, index) => {
                      const treeName = responseData[uuid]?.tree
                        ? responseData[uuid]?.tree?.header?.name
                        : `${index + 1}`;
                      return (
                        <Box
                          key={uuid}
                          position="relative"
                          width="calc(100% - 20px)"
                          role="group"
                          onClick={() => setSelectedUuid(uuid)}
                          cursor="pointer"
                        >
                          <Box
                            p={2}
                            bg={visibleUuid === uuid ? "purple.700" : "bg.surface"}
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={visibleUuid === uuid ? "purple.700" : "purple.300"}
                            position="relative"
                            _hover={{ bg: visibleUuid === uuid ? "purple.700" : "purple.50" }}
                          >
                            <HStack justify="space-between" spacing={0}>
                              <Text
                                color={visibleUuid === uuid ? "white" : "purple.700"}
                                fontSize="xs"
                                fontWeight={visibleUuid === uuid ? "bold" : "medium"}
                                noOfLines={2}
                                textAlign="left"
                                mb={0}
                              >
                                {treeName}
                              </Text>
                              <CloseButton
                                aria-label="Delete tree"
                                size="2xs"
                                variant="ghost"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                color={visibleUuid === uuid ? "purple.800" : "gray.100"}
                                bg={visibleUuid === uuid ? "purple.600" : "transparent"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTree(uuid);
                                }}
                                _hover={{ color: "red.500", bg: "purple.200" }}
                              />
                            </HStack>
                          </Box>
                        </Box>
                      );
                    })}
                </VStack>
              </VStack>

              <Tooltip
                label="Open Library"
                hasArrow
                placement="right"
                bg="gray.50"
                color="black"
                fontSize="sm"
                p={3}
                borderRadius={7}
                arrowSize={9}
                maxW="100vw"
                aria-label="Add Analysis From Library"
              >
                <Button
                  ref={btnRef}
                  onClick={openAddFromLibraryModal}
                  cursor="pointer"
                  gap={2}
                  p={2}
                  borderRadius="lg"
                  borderWidth={1}
                  borderColor="purple.700"
                  color="purple.700"
                  _hover={{
                    bg: "purple.700",
                    color: "white",
                    "& .button-text": {
                      fontWeight: "bold"
                    }
                  }}
                  bg="white"
                  w="100%"
                >
                  <HStack spacing={4} align="center" w="100%" p={3}>
                    <Circle
                      size={7}
                      borderWidth={2}
                      borderColor="purple.700"
                      bg="bg.surface"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <AddIcon color="purple.700" boxSize={4} />
                    </Circle>
                    <Text className="button-text" fontWeight={500} mb={0} fontSize="sm">
                      Add from Library
                    </Text>
                  </HStack>
                </Button>
              </Tooltip>
            </VStack>
          </Box>
        </Box>
      )}
    </Box>
    <AddFromLibraryModal
      isOpen={isOpen}
      onClose={closeAddFromLibraryModal}
    />
    </ExportContextProvider>
  );
};

export default AnalysisNavigationBar;